package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.pages.LearnBookRatingStyle
import com.corbettcode.allaboutrocks.pages.LearnBookTitleStyle
import com.corbettcode.allaboutrocks.toSitePalette
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 400.px

private val PRESS_IMAGE_MAX_HEIGHT = 800.px
private val PRESS_IMAGE_MAX_WIDTH = 900.px
private val PRESS_IMAGE_SMALLER_MAX_WIDTH = 600.px
private val PRESS_IMAGE_VERTICAL_MARGIN = 24.px

private val PROGRAM_POSTER_IMAGE_MAX_HEIGHT = 800.px
private val PROGRAM_POSTER_IMAGE_MAX_WIDTH = 400.px
private val PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN = 24.px

@InitRoute
fun initTypesOfChalcedony(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

/** Named chalcedony varieties, paired with what sets each one apart. */
private val chalcedonyCommonVarieties = listOf(
    "Agate" to "The banded variety, known for its distinctive stripes."
    , "Carnelian / Sard" to "A translucent red to reddish-brown variety."
    , "Chrysoprase" to "An apple-green variety colored by nickel."
    , "Bloodstone" to "A dark green chalcedony with red spots of jasper or iron oxide."
    , "Jasper" to "An opaque, typically multicolored variety. While often grouped with chalcedony, some definitions distinguish it due to its opacity."
    , "Onyx" to "A variety with parallel bands of color, often black and white."
)

private val chalcedonyPhysicalProperties = listOf(
    "Hardness" to "6.5 to 7 on the Mohs scale. This makes it a durable stone that can scratch glass and resist being scratched by a steel knife."
    , "Luster" to "Described as waxy or vitreous (glassy)."
    , "Fracture" to "It breaks with a conchoidal (shell-like) fracture, producing sharp edges. This property made it valuable for early tools."
    , "Appearance" to "It is typically translucent and comes in a wide range of colors, including white, gray, blue, brown, and yellow."
)

@Composable
private fun TypesOfChalcedonyHeadline() {
    val sitePalette = ColorMode.current.toSitePalette()

    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Types of Chalcedony"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfChalcedonyHero() {

    TypesOfChalcedonyHeadline()

    // 1 column on mobile, 2 columns on md+ (text left, image right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/chalcedony/many_faces_of_chalcedony.png"
                    , alt = "how to identify rocks"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Chalcedony is not a single mineral but a catch-all term for a whole family of stones. The key to understanding it is that it is a cryptocrystalline form of quartz. This means its crystals are so microscopic that they are invisible to the naked eye, giving it a smooth, waxy appearance rather than the glassy look of a typical quartz crystal.")
            }

            SpanText(
                "Formation and History"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = Alignment.Start)
            )
            P {
                Text("Chalcedony typically forms at relatively low temperatures when silica-rich water fills cavities or cracks in volcanic or sedimentary rocks and slowly precipitates out. Found worldwide in riverbeds and volcanic regions, it has been used by humans since the Paleolithic era for tools, amulets, and seals. Today, it remains a popular material for jewelry, carvings, and ornamental objects.")
            }

         }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Spacer()

        SpanText(
            "Physical Properties"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Despite its different appearance, its chemical makeup is the same as quartz: silicon dioxide (SiO₂). Here are its key physical traits:")
        }
        Ul(PageListStyle.toAttrs()) {
            chalcedonyPhysicalProperties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        SpanText(
            "Common Varieties\n"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Because chalcedony is a broad category, many familiar gemstones are actually varieties of it, distinguished by their color or patterns:")
        }

        Ul(PageListStyle.toAttrs()) {
            chalcedonyCommonVarieties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }
//
//        SpanText(
//            "Historical and Cultural Uses",
//            PageSectionTitleStyle.toModifier()
//                .color(textColor())
//                .align(alignment = Alignment.Start)
//        )
//        P {
//            Text("Chalcedony has been used since prehistoric times for tools, weapons, and ornaments. In ancient Egypt, it was carved into amulets and scarabs. Native American cultures used it for arrowheads and ceremonial objects. In folklore, chalcedony was considered a protective stone, associated with grounding, courage, and vitality.")
//        }
//
//        SpanText(
//            "Modern Uses",
//            PageSectionTitleStyle.toModifier()
//                .color(textColor())
//                .align(alignment = Alignment.Start)
//        )
//        P {
//            Text("Today, chalcedony is popular in jewelry, cabochons, decorative objects, and tumbled stones. Its durability and wide color range make it a favorite among lapidaries and collectors.")
//        }
//
//        SpanText(
//            "Chalcedony vs. Agate",
//            PageSectionTitleStyle.toModifier()
//                .color(textColor())
//                .align(alignment = Alignment.Start)
//        )
//        P {
//            Text("Both are microcrystalline quartz, but agate is typically translucent and banded, while chalcedony is opaque. The distinction is not always strict; some stones fall between the two.")
//        }
    }
}

@Page("/types_of_rocks/chalcedony")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfChalcedonyPage() {
    TypesOfChalcedonyHero()
}
